// Client-side image compression — resize + re-encode before uploading so
// large phone photos don't time out the /uploads/photo proxy on slow
// mobile networks. Mirrors the backend's PIL pipeline (max 1200px,
// JPEG quality 85) so the round-trip payload is stable.
//
// Usage:
//   const compressed = await compressImage(file);
//   const fd = new FormData();
//   fd.append("file", compressed, compressed.name);
//
// Returns a `File` (or the original file if the browser can't decode it,
// so callers never break on exotic formats).

const MAX_DIMENSION = 1200;
const JPEG_QUALITY = 0.85;
const MIN_BYTES_TO_COMPRESS = 200 * 1024; // < 200KB: skip, already small

export async function compressImage(file, opts = {}) {
  if (!file) return file;
  const maxDim = opts.maxDim || MAX_DIMENSION;
  const quality = opts.quality || JPEG_QUALITY;
  // Small images / non-images short-circuit — no work needed.
  if (!file.type?.startsWith("image/")) return file;
  if (file.size < MIN_BYTES_TO_COMPRESS) return file;

  try {
    const bitmap = await createBitmap(file);
    const { width, height } = fitInside(bitmap.width, bitmap.height, maxDim);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, width, height);
    // ImageBitmap has a close() method; release native memory when done.
    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob) return file;

    // Guardrail — if the "compressed" JPEG somehow ended up larger than
    // the original (rare with tiny GIFs), send the original instead.
    if (blob.size >= file.size) return file;

    const compressedName = renameToJpeg(file.name || "photo.jpg");
    return new File([blob], compressedName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (_e) {
    // Any decode failure → fall back to the raw file so upload still works.
    return file;
  }
}

async function createBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch (_e) { /* fall through to <img> path */ }
  }
  // Fallback for browsers/mobile Safari with quirky createImageBitmap.
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function fitInside(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w > h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function renameToJpeg(name) {
  const dot = name.lastIndexOf(".");
  const base = dot === -1 ? name : name.slice(0, dot);
  return `${base}.jpg`;
}
